import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { kvGet, kvPut, kvSubscribe } from '../services/cloudStorage';

const POLL_MS = 15000; // sync ogni 15 secondi

const lsGet = (k, def) => { try { const v = localStorage.getItem('sirus_'+k); return v ? JSON.parse(v) : def; } catch { return def; } };
const lsSet = (k, v)   => { try { localStorage.setItem('sirus_'+k, JSON.stringify(v)); } catch {} };

// Garantisce che il valore corrisponda al tipo del default.
// Protegge array E oggetti da valori corrotti in localStorage/KV.
function safeValue(v, def) {
  if (v === null || v === undefined) return def;
  if (Array.isArray(def) && !Array.isArray(v)) return def;
  if (def !== null && typeof def === 'object' && !Array.isArray(def) &&
      (typeof v !== 'object' || Array.isArray(v))) return def;
  return v;
}

const normalizeShopItem = (item = {}) => ({
  ...item,
  code: item.code || item.codice || '',
  supplier: item.supplier || item.fornitore || '',
  name: item.name || item.prodotto || '',
  qty: item.qty || item.quantita || item['quantità'] || 1,
});

const normalizeShopList = (items) => Array.isArray(items) ? items.map(normalizeShopItem) : [];

// Hook per dati PERSONALI (nessun polling — solo locale)
// enabled=false (utente non autenticato) salta fetch/write remoti — i dati locali restano
// comunque visibili subito grazie all'inizializzazione sincrona da localStorage.
function useCloudState(key, defaultValue, enabled = true) {
  // Inizializzazione sincrona da localStorage — i dati locali sono visibili immediatamente, anche offline.
  const [data, setData]     = useState(() => safeValue(lsGet(key, defaultValue), defaultValue));
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let mounted = true;
    kvGet(key).then(remote => {
      if (!mounted) return;
      if (remote !== null && remote !== undefined) {
        const safe = safeValue(remote, defaultValue);
        setData(safe); lsSet(key, safe);
      } else {
        // fallback to local cache only if remote missing
        const local = safeValue(lsGet(key, defaultValue), defaultValue);
        if (local !== undefined) setData(local);
      }
      setLoaded(true);
    });
    return () => { mounted = false; };
  }, [key, enabled]);
  useEffect(() => {
    if (!loaded) return;
    lsSet(key, data); kvPut(key, data);
  }, [data, key, loaded]);
  return [data, setData];
}

// Hook per dati CONDIVISI — con polling automatico per sync real-time
// enabled=false (utente non autenticato) salta fetch/SSE/write/polling remoti — `loaded`
// resta false, e SSE/write/polling dipendono tutti da `loaded` quindi restano fermi di conseguenza.
function useSharedState(key, defaultValue, enabled = true) {
  const sharedKey      = `group:${key}`;
  // Inizializzazione sincrona da localStorage — i dati locali sono visibili immediatamente, anche offline.
  const [data, setData]     = useState(() => safeValue(lsGet(sharedKey, defaultValue), defaultValue));
  const [loaded, setLoaded] = useState(false);
  const lastWriteRef      = useRef(0);
  const pendingWriteRef   = useRef(false); // true se kvPut ha fallito (offline)
  const dataRef           = useRef(data);  // sempre aggiornato — usato nel polling
  const initialWriteDone  = useRef(false); // evita write su KV al primo trigger post-load
  useEffect(() => { dataRef.current = data; }, [data]);

  // Caricamento iniziale
  useEffect(() => {
    if (!enabled) return;
    let mounted = true;
    kvGet(sharedKey).then(remote => {
      if (!mounted) return;
      if (remote !== null && remote !== undefined) {
        const safe = safeValue(remote, defaultValue);
        setData(safe); lsSet(sharedKey, safe);
      } else {
        const local = safeValue(lsGet(sharedKey, defaultValue), defaultValue);
        if (local !== undefined) setData(local);
      }
      setLoaded(true);
    });
    return () => { mounted = false; };
  }, [sharedKey, enabled]);

  // SSE subscription for real-time updates (fallback to polling remains)
  useEffect(() => {
    if (!loaded) return;
    const unsub = kvSubscribe(sharedKey, (msg) => {
      // Ignora ping/hello iniziale SSE ({ hello: true }) — non è un aggiornamento dati
      if (!msg || msg.hello) return;
      // Estrai il valore: payload broadcast ha { value, v, updatedAt }
      const remote = msg.value !== undefined ? msg.value : msg;
      if (remote === null || remote === undefined) return;
      const safe = safeValue(remote, defaultValue);
      setData(prev => {
        if (JSON.stringify(prev) === JSON.stringify(safe)) return prev;
        lsSet(sharedKey, safe);
        return safe;
      });
    });
    return () => unsub && unsub();
  }, [sharedKey, loaded]);

  // Scrivi su KV ad ogni cambiamento locale.
  // Al primo trigger post-caricamento (loaded appena diventato true) salviamo solo
  // localStorage ma NON scriviamo su KV — i dati vengono da KV, non dall'utente.
  // Senza questo guard, data=[] (default safeValue) sovrascriveva il KV ripristinato.
  useEffect(() => {
    if (!loaded) return;
    if (!initialWriteDone.current) {
      initialWriteDone.current = true;
      lsSet(sharedKey, data);
      return;
    }
    lastWriteRef.current = Date.now();
    lsSet(sharedKey, data);
    kvPut(sharedKey, data).then(ok => { pendingWriteRef.current = !ok; });
  }, [data, sharedKey, loaded]);

  // Polling per aggiornamenti remoti
  useEffect(() => {
    if (!loaded) return;
    const poll = async () => {
      if (document.hidden) return;
      if (Date.now() - lastWriteRef.current < 5000) return;
      // Se ci sono write offline in sospeso, li spingiamo prima di fare pull
      if (pendingWriteRef.current) {
        const ok = await kvPut(sharedKey, dataRef.current);
        if (ok) pendingWriteRef.current = false;
        return;
      }
      const remote = await kvGet(sharedKey);
      if (remote === null || remote === undefined) return;
      const safe = safeValue(remote, defaultValue);
      setData(prev => {
        if (JSON.stringify(prev) === JSON.stringify(safe)) return prev;
        lsSet(sharedKey, safe);
        return safe;
      });
    };
    const iv = setInterval(poll, POLL_MS);
    const onVisible = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
  }, [sharedKey, loaded]);

  return [data, setData];
}

// ── Tasks — CONDIVISE ─────────────────────────────────────────────────────────
export function useTasks() {
  const DEFAULT = [
    { id:1, text:'Completare MVP Sirus',      priority:'alta',  done:false },
    { id:2, text:'Analisi mercato EUR/USD',    priority:'alta',  done:false },
    { id:3, text:'Pianificare riunione settimanale', priority:'media', done:false },
  ];
  const [tasks, setTasks] = useSharedState('tasks', DEFAULT);
  const addTask    = useCallback((text, priority='media') =>
    setTasks(p => [{ id:Date.now(), text, priority, done:false }, ...p]), [setTasks]);
  const toggleTask = useCallback(id =>
    setTasks(p => p.map(t => t.id===id ? { ...t, done:!t.done } : t)), [setTasks]);
  const deleteTask = useCallback(id =>
    setTasks(p => p.filter(t => t.id !== id)), [setTasks]);
  const editTask   = useCallback((id, text, priority) =>
    setTasks(p => p.map(t => t.id===id ? { ...t, text, priority } : t)), [setTasks]);
  return { tasks, addTask, toggleTask, deleteTask, editTask };
}

// ── My Shop — PERSONALE ───────────────────────────────────────────────────────
export function useShopping(user) {
  const DEFAULT = [
    { id:1, name:'Caffè',         qty:2, destination:'Casa',    supplier:'',       buyDate:'' },
    { id:2, name:'Monitor stand', qty:1, destination:'Ufficio', supplier:'Amazon', buyDate:'' },
  ];
  const [items, setItems] = useCloudState('shopping', DEFAULT, !!user);
  const safeItems = useMemo(() => normalizeShopList(items), [items]);
  const addItem    = useCallback((name, qty=1, destination='', supplier='', buyDate='', code='') =>
    setItems(p => [{ id:Date.now(), code, name, qty, destination, supplier, buyDate }, ...normalizeShopList(p)]), [setItems]);
  const removeItem = useCallback(id =>
    setItems(p => p.filter(i => i.id !== id)), [setItems]);
  const editItem   = useCallback((id, fields) =>
    setItems(p => normalizeShopList(p).map(i => i.id===id ? normalizeShopItem({ ...i, ...fields }) : i)), [setItems]);
  return { items: safeItems, addItem, removeItem, editItem };
}

// ── Group Shop — CONDIVISA ────────────────────────────────────────────────────
export function useGroupShopping(user) {
  const [items, setItems] = useSharedState('shopping', [], !!user);
  const safeItems = useMemo(() => normalizeShopList(items), [items]);
  const addItem    = useCallback((name, qty=1, destination='', supplier='', buyDate='', code='') =>
    setItems(p => [{ id:Date.now(), code, name, qty, destination, supplier, buyDate }, ...normalizeShopList(p)]), [setItems]);
  const removeItem = useCallback(id =>
    setItems(p => p.filter(i => i.id !== id)), [setItems]);
  const editItem   = useCallback((id, fields) =>
    setItems(p => normalizeShopList(p).map(i => i.id===id ? normalizeShopItem({ ...i, ...fields }) : i)), [setItems]);
  return { items: safeItems, addItem, removeItem, editItem };
}

// ── Database prodotti (catalogo) — CONDIVISO tra tutti gli utenti ────────────
// Scrittura (insert/modifica/elimina) riservata ad admin, imposta lato server
// su questa chiave specifica ('products_db') — vedi index.js. Lettura e
// selezione (aggiungi alla lista) restano aperte a tutti i ruoli non-readonly.
export function useProductsDb(user) {
  const [products, setProducts] = useSharedState('products_db', [], !!user);
  return { products, setProducts };
}

// ── Calendario interno — CONDIVISO ────────────────────────────────────────────
export function useCalendar(user) {
  const [events, setEvents] = useSharedState('calendar', [], !!user);
  const addEvent = useCallback((title, date, time, location='', notes='') =>
    setEvents(p => [...p, {
      id: Date.now(),
      title, date, time, location, notes,
      createdAt: Date.now(),
    }].sort((a,b) => (a.date+a.time).localeCompare(b.date+b.time))), [setEvents]);
  const editEvent  = useCallback((id, fields) =>
    setEvents(p => p.map(e => e.id===id ? { ...e, ...fields } : e)
      .sort((a,b) => (a.date+a.time).localeCompare(b.date+b.time))), [setEvents]);
  const deleteEvent = useCallback(id =>
    setEvents(p => p.filter(e => e.id !== id)), [setEvents]);
  return { events, addEvent, editEvent, deleteEvent };
}

// ── Conversation ──────────────────────────────────────────────────────────────
export function useConversation() {
  const [messages, setMessages] = useState([
    { role:'assistant', content:'Sistema online. Come posso aiutarti?' }
  ]);
  const addMessage = useCallback((role, content) =>
    setMessages(p => [...p.slice(-19), { role, content }]), []);
  return { messages, addMessage };
}

// ── Settings ──────────────────────────────────────────────────────────────────
export function useSettings(user) {
  const [wakeWordEnabled, setWakeWordEnabled] = useCloudState('wakeWord', false, !!user);
  return { wakeWordEnabled, setWakeWordEnabled };
}

// ── Task condivise ────────────────────────────────────────────────────────────
export function useSharedTasks(user) {
  const [tasks, setTasks] = useSharedState('tasks', [], !!user);

  const addTask    = useCallback((text, priority='media') =>
    setTasks(p => [{ id:Date.now(), text, priority, done:false }, ...p]), [setTasks]);

  const toggleTask = useCallback(id =>
    setTasks(p => p.map(t => t.id===id ? {...t,done:!t.done} : t)), [setTasks]);

  const deleteTask = useCallback(id =>
    setTasks(p => p.filter(t => t.id !== id)), [setTasks]);

  const editTask   = useCallback((id, text, priority) =>
    setTasks(p => p.map(t => t.id===id ? {...t,text,priority} : t)), [setTasks]);

  return { tasks, addTask, toggleTask, deleteTask, editTask };
}

// ── Group Settings — CONDIVISE ────────────────────────────────────────────────
export function useGroupSettings(user) {
  const [settings, setSettings] = useSharedState('group_settings', { groupName: '' }, !!user);
  const setGroupName = useCallback((name) =>
    setSettings(s => ({ ...s, groupName: name })), [setSettings]);
  return { groupName: settings.groupName || '', setGroupName };
}

// ── Orders — CONDIVISI ───────────────────────────────────────────────────────
const TWELVE_HOURS = 12 * 60 * 60 * 1000;

export function useOrders(user) {
  const [orders, setOrders] = useSharedState('orders', [], !!user);
  const cleanupDone = useRef(false);

  // Cancellazione automatica ordini più vecchi di 12 ore (una sola volta al mount/load)
  useEffect(() => {
    if (cleanupDone.current || orders.length === 0) return;
    cleanupDone.current = true;
    const cutoff = Date.now() - TWELVE_HOURS;
    setOrders(prev => {
      const fresh = prev.filter(o => o.created_at >= cutoff);
      return fresh.length === prev.length ? prev : fresh;
    });
  }, [orders, setOrders]);

  const addOrder = useCallback((fields) =>
    setOrders(p => [{
      id: Date.now(),
      created_at: Date.now(),
      updated_at: Date.now(),
      status: 'active',
      tipo: fields.tipo || '',
      numero_tavolo: fields.numero_tavolo || '',
      starter: fields.starter || '',
      main: fields.main || '',
      dessert: fields.dessert || '',
      note: fields.note || '',
      created_by: fields.created_by || '',
      completed_by: '',
      completed_at: null,
    }, ...p]), [setOrders]);

  const updateOrder = useCallback((id, fields) =>
    setOrders(p => p.map(o => o.id === id ? { ...o, ...fields, updated_at: Date.now() } : o)), [setOrders]);

  const completeOrder = useCallback((id, username) =>
    setOrders(p => p.map(o => o.id === id ? {
      ...o,
      status: 'completed',
      updated_at: Date.now(),
      completed_by: username || '',
      completed_at: Date.now(),
    } : o)), [setOrders]);

  const deleteOrder = useCallback(id =>
    setOrders(p => p.filter(o => o.id !== id)), [setOrders]);

  return { orders, addOrder, updateOrder, completeOrder, deleteOrder };
}

// ── Contacts — CONDIVISI ──────────────────────────────────────────────────────
export function useContacts(user) {
  const [contacts, setContacts] = useSharedState('contacts', [], !!user);
  const addContact    = useCallback((name, phone='', email='', notes='') =>
    setContacts(p => [{ id:Date.now(), name, phone, email, notes }, ...p]), [setContacts]);
  const editContact   = useCallback((id, fields) =>
    setContacts(p => p.map(c => c.id===id ? { ...c, ...fields } : c)), [setContacts]);
  const deleteContact = useCallback(id =>
    setContacts(p => p.filter(c => c.id !== id)), [setContacts]);
  return { contacts, addContact, editContact, deleteContact };
}
